import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import GooglePlayBadge from "@/components/GooglePlayBadge";
import { PRODUCTS } from "@/data/content";
import { useWaitlist } from "@/context/WaitlistContext";

const ACCENT = { coral: "#F28C82", mint: "#B8E3D9", gold: "#F5C86B" };

export default function Products() {
  const { openWaitlist } = useWaitlist();
  return (
    <div data-testid="products-page" className="pt-32">
      <section className="relative overflow-hidden py-12 md:py-20">
        <div className="mesh-bg">
          <div className="mesh-blob" style={{ width: 440, height: 440, background: "#B8E3D9", top: -80, right: -40 }} />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center md:px-12">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#F28C82]">Our products</p>
            <h1 className="mt-6 font-heading text-4xl font-light tracking-tighter text-[#4B2E5A] md:text-6xl">
              A product for every daily decision
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-[#4B2E5A]/70">
              Each Naari product removes decision fatigue from one part of your day — and together, they become an operating system for everyday life.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-8 md:pb-12">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <div className="grid items-stretch gap-6 md:grid-cols-2 md:gap-8">
            {PRODUCTS.map((p, i) => {
              const color = ACCENT[p.accent];
              return (
                <Reveal key={p.slug} delay={i * 0.08} className="h-full">
                  <div className="glass group flex h-full flex-col overflow-hidden rounded-[24px] transition-transform hover:-translate-y-1">
                    <div className="relative h-52 overflow-hidden">
                      <img src={p.image} alt={p.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-medium uppercase tracking-wider text-[#4B2E5A]">{p.available ? "Available Now" : "Coming Soon"}</span>
                    </div>
                    <div className="flex flex-1 flex-col p-8">
                      <div className="flex items-center gap-3">
                        {p.iconImage ? (
                          <img src={p.iconImage} alt={`${p.name} app icon`} className="h-12 w-12 rounded-2xl object-cover" />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-2xl" style={{ background: `${color}22` }}>
                            <p.icon className="h-6 w-6" strokeWidth={1.5} style={{ color }} />
                          </div>
                        )}
                        <h3 className="font-heading text-2xl font-medium text-[#4B2E5A]">{p.name}</h3>
                      </div>
                      <p className="mt-4 leading-relaxed text-[#4B2E5A]/70">{p.cardDescription || p.description}</p>
                      <div className="mt-auto flex flex-wrap gap-3 pt-6">
                        <Link to={`/products/${p.slug}`} data-testid={`products-learn-${p.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#4B2E5A]/10 bg-white/70 px-6 py-3 text-sm font-medium text-[#4B2E5A] hover:bg-white">
                          Learn More <ArrowRight className="h-4 w-4" />
                        </Link>
                        {p.available ? (
                          <GooglePlayBadge height={48} className="self-center" url={p.appUrl} label={p.name} />
                        ) : (
                          <button data-testid={`products-waitlist-${p.slug}`} onClick={() => openWaitlist(p.name)} className="rounded-full bg-[#4B2E5A] px-6 py-3 text-sm font-medium text-[#FCFAF7] hover:bg-[#4B2E5A]/90">
                            Join Waitlist
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <Newsletter />
    </div>
  );
}
